import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { IUserForm, IVestaUserForm } from '../interfaces';
import * as Parse from 'parse';
import { OrganizationService } from './organization.service';

@Injectable()
export class UserService {

    constructor(
        private auth: AuthService,
        private orgService: OrganizationService
    ) {}

    requestPasswordReset(email: string) {
        return Parse.User.requestPasswordReset(email);
    }

    updateUser(formValues: IUserForm) {
        this.auth.currentUser.set('firstname', formValues.firstname.trim());
        this.auth.currentUser.set('lastname', formValues.lastname.trim());
        this.auth.currentUser.set('phone', formValues.phone.trim().replace(/\D+/g, ''));

        if (formValues.password !== '') {
            this.auth.currentUser.setPassword(formValues.password.trim());
        }

        return this.auth.currentUser.save();
    }

    uploadAvatar(avatar: string) {
        return (new Parse.File('avatar.png', { base64: avatar })).save();
    }

    fetchUsers() {
        const query: Parse.Query<Parse.User> = new Parse.Query(Parse.User);
        // q.notEqualTo('objectId', Parse.User.current().id);
        query.equalTo('organization', this.orgService.organization);
        query.limit(500);

        return query.find();
    }

    getCustomerCountForUser(user: Parse.User) {
        const query: Parse.Query = new Parse.Query('Customer');

        query.notEqualTo('mailchimpStatus', 'cleaned');
        query.equalTo('preferredAgent', user);
        query.equalTo('organization', this.orgService.organization);

        return query.count();
    }

    fetchNonAssistantUsers() {
        const query: Parse.Query = new Parse.Query(Parse.User);

        query.notEqualTo('type', 'AAA');
        query.equalTo('organization', this.orgService.organization);

        return query.find();
    }

    fetchAssistants() {
        const query: Parse.Query = new Parse.Query(Parse.User);

        query.equalTo('type', 'AAA');
        query.equalTo('organization', this.orgService.organization);

        return query.find();
    }

    getAssistantsForUser(user: Parse.User): Promise<Parse.User[]> {
        return Parse.Object.fetchAllIfNeeded(user.get('assistants'));
    }

    fetchViewOnlyAdminCount() {
        const query: Parse.Query = new Parse.Query(Parse.User);

        query.equalTo('type', 'View-Only-Admin');
        query.equalTo('organization', this.orgService.organization);

        return query.count();
    }

    fetchCallCount(user: Parse.Object) {
        const query: Parse.Query = new Parse.Query('CallLog');

        query.equalTo('agent', user);
        query.equalTo('organization', this.orgService.organization);

        return query.count();
    }

    fetchCallHistory(user: Parse.Object, pageIndex: number, pageSize: number) {
        const query: Parse.Query = new Parse.Query('CallLog');

        query.equalTo('agent', user);
        query.equalTo('organization', this.orgService.organization);

        query.include('customer');
        query.include('property');
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);
        query.descending('createdAt');

        return query.find();
    }

    fetchUsersForFilterModal() {
        const query: Parse.Query = new Parse.Query(Parse.User);

        query.notEqualTo('objectId', this.auth.currentUser.id);
        query.equalTo('organization', this.orgService.organization);
        query.select('firstname', 'lastname');
        query.ascending('firstname');

        return query.find();
    }

    createNewUser(email: string, userType: string) {
        const user = new Parse.User();
        user.set('username', email);
        user.set('email', email);
        user.set('password', Math.random().toString(16) + Math.random());
        user.set('organization', this.orgService.organization);
        user.set('invitedBy', this.auth.currentUser);

        if (!this.orgService.isPI) {
            if (userType === 'Owner-Admin') {
                user.set('type', 'Admin');
                user.set('isOwner', true);
            } else {
                user.set('type', userType || 'SA');
                user.set('isOwner', false);
            }
        } else {
            if (userType === 'Owner-Admin') {
                user.set('type', 'Admin');
                user.set('isOwner', true);
            } else {
                user.set('type', userType || 'PM');
                user.set('isOwner', false);
            }
        }

        if (userType === 'Owner-Admin' || userType === 'Admin') {
            user.set('receiveTCEmails', true);
        } else {
            user.set('receiveTCEmails', false);
        }

        return user.save(null, { cascadeSave: false })
    }

    inviteUser(email: string) {
        return Parse.User.requestPasswordReset(email);
    }

    hasLoggedInBefore(username: string) {
        return Parse.Cloud.run('hasLoggedInBefore', { username: username });
    }

    saveUser(user: Parse.User, form: IVestaUserForm) {
        if (user.getUsername() !== form.email) {
            user.setEmail(form.email);
            user.setUsername(form.email);
        }

        user.set('firstname', form.firstname);
        user.set('lastname', form.lastname);

        if (form.phone) {
            user.set('phone', form.phone.replace(/\D+/g, ''));
        } else {
            user.unset('phone');
        }

        // set isOwner flag
        if (form.type === 'Owner-Admin') {
            user.set('type', 'Admin');
            user.set('isOwner', true);
        } else {
            user.set('type', form.type);
            user.set('isOwner', false);
        }

        // set isViewAdmin flag, for admin users only
        if (form.type === 'Admin' || form.type === 'Owner-Admin') {
            if (form.isViewAdmin) {
                user.set('isViewAdmin', true);
            } else {
                user.set('isViewAdmin', false);
            }
        }

        user.set('deactivated', form.deactivated);
        user.set('receiveTCEmails', form.receiveTCEmails);
        user.set('receiveAssistantCC', form.receiveAssistantCC);

        return user.save(null, { cascadeSave: false });
    }

    deleteUser(user: Parse.User) {
        return Parse.Cloud.run('deleteUser', { id: user.id });
    }

    transferCustomers(source: Parse.User, destination: Parse.User, transfer?: Parse.Object) {
        return Parse.Cloud.run('initCustomerTransfer', {
            sourceUserId: source.id,
            destinationUserId: destination.id,
            revertOf: transfer?.id
        });
    }

    revertCustomerTransfer(transfer: Parse.Object) {
        return Parse.Cloud.run('initRevertTransfer', {
            transferId: transfer.id
        });
    }

    async assignAssistantToUser(user: Parse.User, assistant: Parse.User) {
        const query: Parse.Query = new Parse.Query(Parse.Role);

        query.equalTo('name', user.id + '_Assistants');
        query.equalTo('organization', this.orgService.organization);

        const role: Parse.Role = (await query.first()) as Parse.Role;
        role.getUsers().add(assistant);

        user.addUnique('assistants', assistant);
        assistant.set('assistantOf', user);

        return Parse.Object.saveAll([user, assistant, role as Parse.Role]);
    }

    async removeAssistantFromUser(user: Parse.User, assistant: Parse.User) {
        // Remove the Assistant from the user's assistants role
        const query: Parse.Query = new Parse.Query(Parse.Role);

        query.equalTo('name', user.id + '_Assistants');
        query.equalTo('organization', this.orgService.organization);

        const role: Parse.Role = (await query.first()) as Parse.Role;
        role.getUsers().remove(assistant);

        user.remove('assistants', assistant);
        assistant.unset('assistantOf');

        await Parse.Object.saveAll([user, assistant, role]);
    }

    fetchUsersForResponsibleForSaleModal() {
        const query: Parse.Query = new Parse.Query(Parse.User);

        query.equalTo('organization', this.orgService.organization);
        query.select('firstname', 'lastname');
        query.ascending('firstname');

        return query.find();
    }
}
